import React, { useCallback, useEffect, useState } from "react";
import { PageHeader, LoadingState, ErrorState, PhxButton } from "@/design-system";
import { api, formatApiError } from "@/lib/api";
import { Can } from "@/lib/permissions";

/**
 * Admin Settings: feature toggles (company_settings.feature_flags) and
 * per-role permissions. Backend enforces `settings.manage` / `roles.manage`.
 */
function FeatureToggles() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/features");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    setBusy(key);
    try {
      const r = await api.put("/settings/features", { [key]: value });
      setData((d) => ({ ...d, flags: r.data.flags }));
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (error && !data) return <ErrorState message={error} retry={load} testid="settings-features-error" />;
  if (!data) return <LoadingState testid="settings-features-loading" />;

  return (
    <section data-testid="settings-features">
      <h2 className="text-sm font-semibold uppercase tracking-wide mb-3">Features</h2>
      {error && <p className="text-sm text-red-600 mb-2" role="alert">{error}</p>}
      <ul className="divide-y border bg-white">
        {Object.keys(data.flags).map((key) => (
          <li key={key} className="flex items-center justify-between p-3">
            <label htmlFor={`flag-${key}`} className="text-sm">
              <span className="font-medium">{key}</span>
              <span className="block text-xs text-slate-500">{data.descriptions?.[key]}</span>
            </label>
            <input
              id={`flag-${key}`}
              data-testid={`flag-${key}`}
              type="checkbox"
              checked={!!data.flags[key]}
              disabled={busy === key}
              onChange={(e) => toggle(key, e.target.checked)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function RolePermissions() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/roles");
      setData(r.data);
      setDrafts(Object.fromEntries(r.data.roles.map((x) => [x.name, new Set(x.permissions || [])])));
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flip = (role, perm) =>
    setDrafts((d) => {
      const next = new Set(d[role]);
      next.has(perm) ? next.delete(perm) : next.add(perm);
      return { ...d, [role]: next };
    });

  const save = async (role) => {
    try {
      await api.put(`/settings/roles/${encodeURIComponent(role)}/permissions`, {
        permissions: [...drafts[role]].sort(),
      });
      setSaved(role);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (error && !data) return <ErrorState message={error} retry={load} testid="settings-roles-error" />;
  if (!data) return <LoadingState testid="settings-roles-loading" />;

  return (
    <section data-testid="settings-roles" className="mt-8">
      <h2 className="text-sm font-semibold uppercase tracking-wide mb-3">Roles & permissions</h2>
      {error && <p className="text-sm text-red-600 mb-2" role="alert">{error}</p>}
      {data.roles.map((role) => (
        <details key={role.name} className="border bg-white mb-2" data-testid={`role-${role.name}`}>
          <summary className="p-3 cursor-pointer text-sm font-medium">
            {role.name} <span className="text-xs text-slate-500">({drafts[role.name]?.size ?? 0} permissions)</span>
          </summary>
          <div className="p-3 grid gap-1 sm:grid-cols-2">
            {data.available_permissions.map((perm) => (
              <label key={perm} className="text-xs flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={role.name === "admin"}
                  checked={drafts[role.name]?.has(perm) || false}
                  onChange={() => flip(role.name, perm)}
                />
                {perm}
              </label>
            ))}
          </div>
          {role.name !== "admin" && (
            <div className="p-3 border-t flex items-center gap-3">
              <PhxButton onClick={() => save(role.name)} data-testid={`save-role-${role.name}`}>
                Save {role.name}
              </PhxButton>
              {saved === role.name && <span className="text-xs text-green-700">Saved</span>}
            </div>
          )}
        </details>
      ))}
    </section>
  );
}

function UserRoles() {
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([api.get("/settings/users"), api.get("/settings/roles")]);
      setUsers(u.data.items);
      setRoles(r.data.roles.map((x) => x.name));
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u, role, on) => {
    const next = on ? [...new Set([...u.roles, role])] : u.roles.filter((x) => x !== role);
    setBusy(u.id);
    try {
      const r = await api.put(`/settings/users/${u.id}/roles`, { roles: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, roles: r.data.roles } : x)));
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (error && !users) return <ErrorState message={error} retry={load} testid="settings-users-error" />;
  if (!users) return <LoadingState testid="settings-users-loading" />;
  return (
    <section data-testid="settings-users">
      <h2 className="text-sm font-semibold uppercase tracking-wide mb-3">Users and roles</h2>
      {error && <p className="text-sm text-red-600 mb-2" role="alert">{error}</p>}
      <ul className="divide-y border bg-white">
        {users.map((u) => (
          <li key={u.id} className="p-3 text-sm" data-testid={`user-${u.id}`}>
            <span className="font-medium">{u.name}</span>
            <span className="text-xs text-slate-500"> {u.email || u.phone}</span>
            <div className="flex flex-wrap gap-3 mt-1">
              {roles.map((r) => (
                <label key={r} className="flex items-center gap-1">
                  <input type="checkbox" aria-label={`${u.id}-${r}`} checked={u.roles.includes(r)}
                    disabled={busy === u.id} onChange={(e) => toggle(u, r, e.target.checked)} />
                  {r}
                </label>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Devices() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems((await api.get("/devices")).data.items);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (d) => {
    if (!window.confirm(`Revoke "${d.name}"? It will be signed out and cannot sign in again.`)) return;
    try {
      await api.post(`/devices/${d.id}/revoke`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (error && !items) return <ErrorState message={error} retry={load} testid="settings-devices-error" />;
  if (!items) return <LoadingState testid="settings-devices-loading" />;
  return (
    <section data-testid="settings-devices">
      <h2 className="text-sm font-semibold uppercase tracking-wide mb-3">Devices</h2>
      {error && <p className="text-sm text-red-600 mb-2" role="alert">{error}</p>}
      <ul className="divide-y border bg-white">
        {items.map((d) => (
          <li key={d.id} className="flex items-center justify-between p-3 text-sm" data-testid={`device-${d.id}`}>
            <span>
              <span className="font-medium">{d.name}</span>
              <span className="block text-xs text-slate-500">
                last seen {d.last_seen_at?.slice(0, 16).replace("T", " ")}
                {d.revoked_at ? " · revoked" : ""}
              </span>
            </span>
            {!d.revoked_at && <PhxButton variant="secondary" onClick={() => revoke(d)}>Revoke</PhxButton>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function SettingsPage() {
  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Administration"
        title="Settings"
        description="Switch optional features on or off and choose which roles can do what, including approving client requests."
      />
      <Can perm="settings.manage" fallback={<p className="text-sm text-slate-500">You do not have permission to change settings.</p>}><FeatureToggles /></Can>
      <Can perm="roles.manage"><RolePermissions /></Can>
      <Can perm="roles.manage"><UserRoles /></Can>
      <Can perm="settings.manage"><Devices /></Can>
    </div>
  );
}
